export enum ServiceId {
    ICAP = 'icap',
    MDSS = 'mdss',
}

import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'hooks/useSearchParams';
import { dayjs } from 'services/dayjs';
import { AdvancedAnalysisTabs } from 'services/interface';
import { EngineType, GateId } from 'types/Workflows';

const CACHED_KEYS: Array<keyof QueryFilter> = ['startDate', 'endDate', 'organizationIds'];

export enum CveSeverity {
    CRITICAL = 'CRITICAL',
    IMPORTANT = 'IMPORTANT',
    MODERATE = 'MODERATE',
    LOW = 'LOW',
}

export enum ScanStatus {
    Aborted = 'Aborted',
    Allowed = 'Allowed',
    Blocked = 'Blocked',
    Processing = 'Processing',
}

export enum SortDir {
    Asc = 'asc',
    Desc = 'desc',
}

export enum PageKey {
    Default = 'DEFAULT_FILTERS',
    Dashboard = 'DASHBOARD_FILTERS',
    Reports = 'REPORTS_FILTERS',
    Workflows = 'WORKFLOWS_FILTERS',
    ExploreData = 'EXPLORE_DATA_FILTERS',
}

export interface QueryFilter {
    offset: number;
    limit: number;
    cursor: string;
    startDate: string;
    endDate: string;
    sortDir: SortDir | '';
    sortBy: string;
    search: string;
    organizationIds: string[];
    fileTypeId: string;
    cveSeverity: CveSeverity | '';
    scanStatus: ScanStatus | '';
    advTab: AdvancedAnalysisTabs;
    engineType: EngineType | '';
    gateId: GateId;
    tags: string;
    lastSandboxId: string;
    serviceId: ServiceId | '';
}

type Callback = (filter: Partial<QueryFilter>) => Partial<QueryFilter>;

export interface QueryFilterHook extends QueryFilter {
    setFilter: (filter: Partial<QueryFilter>) => void;
    clearFilter: (...keys: (keyof QueryFilter)[]) => void;
    setGlobal: (callback: Callback) => void;
    clearGlobal: (...keys: (keyof QueryFilter)[]) => void;
}

export enum QueryFilterNS {
    ExploreData = 'exploreData',
    DetectionTrend = 'detectionTrend',
    AlinTrend = 'alinTrend',
}

export interface QueryFilterOptions {
    pageKey?: PageKey;
    ns?: QueryFilterNS;
}

const getNSKey = (key: keyof QueryFilter, ns?: QueryFilterNS) => {
    if (ns) {
        return `${ns}[${key}]`;
    }

    return key;
};

const getCache = ({ pageKey = PageKey.Default }: { pageKey?: PageKey }) => {
    if (typeof window === 'undefined') {
        return new URLSearchParams();
    }

    const cache = window.sessionStorage.getItem(pageKey);

    return new URLSearchParams(cache ?? '');
};

const setCache = ({ pageKey = PageKey.Default, params }: { pageKey?: PageKey; params: URLSearchParams }) => {
    if (typeof window === 'undefined') {
        return;
    }

    const searchParams = new URLSearchParams(params);

    Array.from(searchParams.keys()).forEach((key) => {
        if (!CACHED_KEYS.includes(key as keyof QueryFilter)) {
            searchParams.delete(key);
        }
    });

    window.sessionStorage.setItem(pageKey, searchParams.toString());
};

const parseParams = ({
    filter,
    params,
    ns,
}: {
    filter: Partial<QueryFilter>;
    params: URLSearchParams;
    ns?: QueryFilterNS;
}) => {
    const parsed = new URLSearchParams(params);
    const offsetKey = getNSKey('offset', ns);
    const cursorKey = getNSKey('cursor', ns);

    for (const key of Object.keys(filter) as (keyof QueryFilter)[]) {
        const nsKey = getNSKey(key, ns);
        const value = (filter as QueryFilter)[key];

        if (Array.isArray(value)) {
            parsed.delete(nsKey);
            value.forEach((val) => {
                parsed.append(nsKey, val.toString());
            });
        } else if (value) {
            parsed.set(nsKey, value.toString());
        }
    }

    const shouldReset =
        filter.limit !== undefined || (Array.isArray(filter?.organizationIds) && filter.organizationIds.length > 0);

    if (shouldReset && filter.offset === undefined) {
        parsed.delete(offsetKey);
    }

    if (shouldReset && filter.cursor === undefined) {
        parsed.delete(cursorKey);
    }

    return parsed;
};

const mergeParams = (prev: URLSearchParams, current: URLSearchParams) => {
    const merged = new URLSearchParams(current);

    for (const key of Array.from(prev.keys())) {
        const value = prev.getAll(key);

        if (!merged.has(key) && value.length > 1) {
            for (const val of value) {
                merged.append(key, val);
            }
        } else if (!merged.has(key) && value.length === 1) {
            merged.set(key, value[0]);
        }
    }

    return merged;
};

const getFilter = (params: URLSearchParams, ns?: QueryFilterNS) => {
    const filter = {} as Partial<Record<keyof QueryFilter, string | string[]>>;

    for (const key of Array.from(params.keys()) as (keyof QueryFilter)[]) {
        const nsKey = getNSKey(key, ns);
        const value = params.getAll(nsKey);

        if (value.length > 1) {
            filter[key] = value;
        } else if (value.length === 1) {
            filter[key] = value[0];
        }
    }

    return filter as Partial<QueryFilter>;
};

export const useQueryFilter = ({ pageKey, ns }: QueryFilterOptions = {}): QueryFilterHook => {
    const [searchParams, setSearchParams] = useSearchParams();

    const offsetKey = getNSKey('offset', ns);
    const limitKey = getNSKey('limit', ns);
    const cursorKey = getNSKey('cursor', ns);
    const startDateKey = getNSKey('startDate', ns);
    const endDateKey = getNSKey('endDate', ns);
    const sortDirKey = getNSKey('sortDir', ns);
    const sortByKey = getNSKey('sortBy', ns);
    const searchKey = getNSKey('search', ns);
    const organizationIdsKey = getNSKey('organizationIds', ns);
    const fileTypeIdKey = getNSKey('fileTypeId', ns);
    const cveSeverityKey = getNSKey('cveSeverity', ns);
    const scanStatusKey = getNSKey('scanStatus', ns);
    const advTabKey = getNSKey('advTab', ns);
    const engineTypeKey = getNSKey('engineType', ns);
    const gateIdKey = getNSKey('gateId', ns);
    const tagsKey = getNSKey('tags', ns);
    const lastSandboxIdKey = getNSKey('lastSandboxId', ns);
    const serviceIdKey = getNSKey('serviceId', ns);

    const offset = Number(searchParams.get(offsetKey)) || 0;
    const limit = Number(searchParams.get(limitKey)) || 10;
    const cursor = searchParams.get(cursorKey) || '';
    const rawStartDate = searchParams.get(startDateKey);
    const rawEndDate = searchParams.get(endDateKey);
    const sortDir = (searchParams.get(sortDirKey) as SortDir) || '';
    const sortBy = searchParams.get(sortByKey) || '';
    const search = searchParams.get(searchKey) || '';
    const organizationIds = searchParams.getAll(organizationIdsKey);
    const fileTypeId = searchParams.get(fileTypeIdKey) || '';
    const cveSeverity = (searchParams.get(cveSeverityKey) as CveSeverity) || '';
    const scanStatus = (searchParams.get(scanStatusKey) as ScanStatus) || '';
    const advTab = (searchParams.get(advTabKey) as AdvancedAnalysisTabs) || AdvancedAnalysisTabs.FileDetails;
    const engineType = (searchParams.get(engineTypeKey) as EngineType) || '';
    const gateId = (Number(searchParams.get(gateIdKey)) as GateId) || GateId.Zero;
    const tags = searchParams.get(tagsKey) || '';
    const lastSandboxId = searchParams.get(lastSandboxIdKey) || '';
    const serviceId = (searchParams.get(serviceIdKey) as ServiceId) || '';

    const { startDate, endDate } = useMemo(() => {
        const now = dayjs().utc();
        let startDate = now.subtract(7, 'days').toISOString();
        let endDate = now.toISOString();

        if (rawStartDate && dayjs(rawStartDate).isValid()) {
            startDate = dayjs(rawStartDate).utc().toISOString();
        }

        if (rawEndDate && dayjs(rawEndDate).isValid()) {
            endDate = dayjs(rawEndDate).utc().toISOString();
        }

        return { startDate, endDate };
    }, [rawStartDate, rawEndDate]);

    useEffect(() => {
        const cache = getCache({ pageKey });
        const merged = mergeParams(cache, searchParams);

        if (merged.toString() !== searchParams.toString()) {
            setSearchParams(() => merged);
        }
        // deps intentionally [pageKey]: hydrate the URL from cache once per page; adding searchParams/setSearchParams would loop since this effect writes searchParams
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageKey]);

    const setFilter: QueryFilterHook['setFilter'] = (filter) => {
        setSearchParams((prev) => {
            const params = parseParams({ filter, params: prev, ns });

            setCache({ pageKey, params });

            return params;
        });
    };

    const clearFilter: QueryFilterHook['clearFilter'] = (...keys) => {
        setSearchParams((prev) => {
            keys.forEach((key) => {
                const nsKey = getNSKey(key, ns);
                prev.delete(nsKey);
            });

            setCache({ pageKey, params: prev });

            return prev;
        });
    };

    const setGlobal: QueryFilterHook['setGlobal'] = (callback) => {
        setSearchParams((prev) => {
            for (const pageKey of Object.values(PageKey)) {
                const cache = getCache({ pageKey });
                const filter = getFilter(cache, ns);
                const newFilter = callback(filter);
                const params = parseParams({ filter: newFilter, params: cache, ns });

                setCache({ pageKey, params });
            }

            const filter = getFilter(prev, ns);
            const newFilter = callback(filter);

            return parseParams({ filter: newFilter, params: prev, ns });
        });
    };

    const clearGlobal: QueryFilterHook['clearGlobal'] = (...args) => {
        const keys = args.length === 0 ? CACHED_KEYS : args;

        setSearchParams((prev) => {
            for (const pageKey of Object.values(PageKey)) {
                const cache = getCache({ pageKey });

                keys.forEach((key) => {
                    const nsKey = getNSKey(key, ns);
                    cache.delete(nsKey);
                });

                setCache({ pageKey, params: cache });
            }

            keys.forEach((key) => {
                const nsKey = getNSKey(key, ns);
                prev.delete(nsKey);
            });

            return prev;
        });
    };

    return {
        offset,
        limit,
        cursor,
        startDate,
        endDate,
        sortDir,
        lastSandboxId,
        sortBy,
        search,
        organizationIds,
        fileTypeId,
        cveSeverity,
        scanStatus,
        advTab,
        engineType,
        gateId,
        tags,
        serviceId,
        setFilter,
        clearFilter,
        setGlobal,
        clearGlobal,
    };
};
